import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Components
import useToast from '../../components/molecules/Toast/useToast';

// Queries
import { useTableFiltersMutation, TABLE_FILTERS_QUERY_KEY } from './tableFiltersQueries';
import { useManagerGenericMutation } from '../genericUrl/genericUrlQueries';
import { TABLE_QUERY_KEY } from '../table/tableQueries';

/**
 * Custom hook for optimistic updates of manager table filters filter values
 * @param {string} path - The page path for the query key
 * @param {import('@tanstack/react-query').UseMutationOptions<ApiManagerTableFiltersCreate, Error, ApiManagerTableFiltersCreate, unknown>} [options = {}] - The mutation options
 * @param {string | null} [contexte] - The context (optional)
 * @returns {import('@tanstack/react-query').UseMutationResult<ApiManagerTableFiltersCreate, Error, ApiManagerTableFiltersCreate, unknown>} The mutation with optimistic updates
 */
export const useTableFiltersOptimistic = (path, options = {}, contexte = null) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = options;

  return useTableFiltersMutation(path, {
    ...restOptions,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path, contexte] });

      /** @type {ApiManagerTableFilters} */
      const old = queryClient.getQueryData([TABLE_FILTERS_QUERY_KEY, path, contexte]);

      const optimisticManagerTableFilters = {
        ...old,
        columnFilters: data?.columnFilters ?? old?.columnFilters,
        sortBy: {
          ...old?.sortBy,
          criteriaValues: data?.sortBy?.criteriaValues ?? old?.sortBy?.criteriaValues,
        },
        filterValues: data?.filterValues ?? old?.filterValues,
        groupBy: {
          ...old?.groupBy,
          selectedOption: data?.groupBy?.selectedOption ?? old?.groupBy?.selectedOption,
        },
        // POST search omits GET-only fields (e.g. options); merge so the toolbar stays mounted.
        search: {
          ...old?.search,
          ...data?.search,
        },
        // Propagate pageSize so the per-page selector and the range label switch instantly
        // instead of waiting for the POST /tableFilters round-trip + invalidation.
        pageSize: data?.pageSize ?? old?.pageSize,
        personalViews: {
          ...old?.personalViews,
          selectedOption: (() => {
            const value = data?.personalViews?.selectedOption ?? old?.personalViews?.selectedOption;
            return value === '' ? null : value;
          })(),
        },
      };

      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, path, contexte],
        () => ({ ...optimisticManagerTableFilters }),
      );

      return { optimisticManagerTableFilters };
    },
    onError: (
      /** @type {Error} */ error,
      /** @type {ApiManagerTableFiltersCreate} */ variables,
    ) => {
      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, path, contexte],
        () => variables,
      );

      notify({
        message: error?.message || t('toast.tableFilters.error'),
        type: 'alert',
      });
      onError?.(error, variables);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path, contexte] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  }, contexte);
};

/**
 * Custom hook for optimistic updates of manager table filters column management (add or remove columns)
 * @param {string} path - The page path for the query key
 * @param {import('@tanstack/react-query').UseMutationOptions<ApiManagerTableFiltersCreate, Error, ApiManagerTableFiltersCreate, unknown>} [options = {}] - The mutation options
 * @param {string | null} [contexte] - The context (optional)
 * @returns {import('@tanstack/react-query').UseMutationResult<ApiManagerTableFiltersCreate, Error, ApiManagerTableFiltersCreate, unknown>} The mutation with optimistic updates
 */
export const useTableFiltersOptimisticColumnManagement = (path, options = {}, contexte = null) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = options;

  return useTableFiltersMutation(path, {
    ...restOptions,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path, contexte] });

      /** @type {ApiManagerTableFilters} */
      const old = queryClient.getQueryData([TABLE_FILTERS_QUERY_KEY, path, contexte]);

      const optimisticManagerTableFilters = {
        ...old,
        columnFilters: data?.columnFilters ?? old?.columnFilters,
        personalViews: {
          ...old?.personalViews,
          selectedOption: (() => {
            const value = data?.personalViews?.selectedOption ?? old?.personalViews?.selectedOption;
            return value === '' ? null : value;
          })(),
        },
      };
      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, path, contexte],
        () => ({ ...optimisticManagerTableFilters }),
      );

      const columnsFiltersVisible = data?.columnFilters?.filter((columnFilter) => columnFilter.visible);
      /**
       * @type {TableQueryDataTuple[]}
       */
      const arrayTableData = /** @type {TableQueryDataTuple[]} */ (queryClient.getQueriesData({
        queryKey: [TABLE_QUERY_KEY],
        type: 'active',
      }));
      const activeTableData = arrayTableData?.[0]?.[1];

      // If column is visible and exist in activeTableData keep column data, otherwise
      // push new column with data from columnFiltersVisible for optimistic update
      /** @type {Array<ApiManagerTableColumns>} */
      const newTableColumns = columnsFiltersVisible?.reduce((acc, columnFilter) => {
        const columnFromTable = activeTableData?.columns?.find((column) => column.id === columnFilter.id);
        if (columnFromTable) {
          acc.push(columnFromTable);
        } else {
          acc.push({
            align: 'left',
            bold: false,
            id: columnFilter.id,
            italic: false,
            lib: columnFilter.tableLib,
            sortable: true,
            statValue: '',
            type: 'text',
            validation: /** @type {Array<ApiManagerTableValidation>} */ ([]),
            width: columnFilter.width,
            verticalAlign: 'middle',
            headers: [
              {
                lib: columnFilter.tableLib,
                width: columnFilter.width,
                colspan: 1,
                align: 'left',
                backgroundColor: null,
                textDecoration: { strikeThrough: false, underline: false },
              },
            ],
            isAddingColumnOptimistic: true,
          });
        }
        return acc;
      }, []) ?? [];

      // Update the cache with optimistic data
      const queryKey = arrayTableData[0]?.[0];
      if (queryKey) {
        queryClient.setQueryData(queryKey, { ...activeTableData, columns: newTableColumns });
      }

      return { optimisticManagerTableFilters };
    },
    onError: (
      /** @type {Error} */ error,
      /** @type {ApiManagerTableFiltersCreate} */ variables,
    ) => {
      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, path, contexte],
        () => variables,
      );

      notify({
        message: error?.message || t('toast.tableFilters.error'),
        type: 'alert',
      });
      onError?.(error, variables);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path, contexte] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  }, contexte);
};

/**
 * Custom hook for optimistic updates of manager table filters personal views
 * @param {string} url - The URL to call
 * @param {string} path - The page path for the query key
 * @param {string | null} [contexte] - The context (optional)
 * @returns {import('@tanstack/react-query').UseMutationResult} The mutation with optimistic updates
 */
export const useTableFiltersOptimisticPersonalViews = (url, path, contexte = null) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  return useManagerGenericMutation(url, {
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path, contexte] });

      const optimisticManagerTableFilters = {
        ...data,
        personalViews: {
          ...data?.personalViews,
          options: data?.personalViews?.options ?? [],
          defaultOption: data?.personalViews?.defaultOption,
        },
      };

      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, path, contexte],
        (/** @type {ApiManagerTableFilters} */ old) => ({
          ...old,
          personalViews: {
            ...old?.personalViews,
            ...optimisticManagerTableFilters?.personalViews,
          },
        }),
      );

      return { optimisticManagerTableFilters };
    },
    onError: (
      /** @type {Error} */ error,
      /** @type {ApiManagerTableFiltersCreate} */ variables,
    ) => {
      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, path, contexte],
        () => variables,
      );

      notify({
        message: error?.message || t('toast.tableFilters.error'),
        type: 'alert',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path, contexte] });
    },
  });
};
